// ALOP: shared pieces — action buttons (with the reason prompt when the law asks for one), partner picker with ANAF lookup,
// budget line picker, money input, the phase bar and the money bar of a dosar.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../api';
import { Combobox, Field, Modal, fmtDate, useToast } from '../ui';
import { Icon } from '../ui/Icon';
import type { Action, BudgetLine, Dossier, Partner, Phase } from './types';
import { PHASES, alopChanged, lei, parseAmount } from './types';

/** Runs an action on an ALOP object; actions that need a reason open a small prompt first. */
export function useAct() {
  const toast = useToast();
  const [prompt, setPrompt] = useState<{ a: Action; run: (comment?: string) => Promise<void> } | null>(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (a: Action, path: string, onDone: (r: any) => void, extra?: Record<string, any>) => {
    const run = async (c?: string) => {
      setBusy(true);
      try { const r = await api.post(path, { action: a.code, comment: c, ...(extra || {}) }); onDone(r); alopChanged(); toast(doneText(a.code), 'ok'); setPrompt(null); setComment(''); }
      catch (e: any) { toast(e.message, 'error'); }
      finally { setBusy(false); }
    };
    if (a.comment) { setPrompt({ a, run }); return; }
    await run();
  };
  const node = prompt && (
    <Modal title={prompt.a.label} onClose={() => setPrompt(null)} size="sm" icon="edit"
      footer={<><span className="grow" /><button className="btn" onClick={() => setPrompt(null)}>Renunță</button>
        <button className={'btn ' + (DANGER.includes(prompt.a.code) ? 'danger' : 'primary')} disabled={busy || (prompt.a.code !== 'CLOSE' && !comment.trim())} onClick={() => prompt.run(comment.trim() || undefined)}>{prompt.a.label}</button></>}>
      <Field label={prompt.a.code === 'OVERRIDE' ? 'Actul de decizie internă — nr., data și motivarea (art. 21 din OG 119/1999; art. 28 din normele OMF 1140/2025)' : prompt.a.code === 'CLOSE' ? 'Observații (opțional)' : 'Motivul'} required={prompt.a.code !== 'CLOSE'}>
        <textarea className="textarea" autoFocus value={comment} onChange={e => setComment(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && (comment.trim() || prompt.a.code === 'CLOSE')) prompt.run(comment.trim() || undefined); }} />
      </Field>
    </Modal>
  );
  return { act, node, busy };
}
const DANGER = ['CFP_REFUSE', 'CFPD_REFUSE', 'REJECT', 'CANCEL'];
function doneText(code: string) {
  return ({ SUBMIT: 'Semnat și transmis', CAB: 'Secțiunea B completată — sumele sunt rezervate', AVIZ: 'Verificat / avizat', CAB_OK: 'Coloanele CAB confirmate — trimis la CFP',
    CFP_OK: 'Viza CFP acordată', CFPD_OK: 'Viza CFP delegat acordată', CFP_REFUSE: 'Viza a fost refuzată', CFPD_REFUSE: 'Viza a fost refuzată', APPROVE: 'Aprobat', SIGN: 'Angajamentul legal a fost emis',
    REGISTER: 'Angajamentul a fost înregistrat', OVERRIDE: 'Aprobat pe propria răspundere', REJECT: 'Neaprobat', RETURN: 'Restituit', CANCEL: 'Anulat', REVISE: 'Revizuire nouă deschisă',
    CLOSE: 'Revizuirea de închidere a fost trimisă la CAB', CARRY: 'Revizuirea pentru noul an a fost trimisă la CAB', BUN_PLATA: '„Bun de plată” acordat', PAY: 'Plata a fost înregistrată' } as Record<string, string>)[code] || 'Gata';
}

export function ActionButtons({ actions, onRun, busy, size = 'sm', skip = [] }: { actions: Action[]; onRun: (a: Action) => void; busy?: boolean; size?: 'sm' | ''; skip?: string[] }) {
  const list = actions.filter(a => !skip.includes(a.code));
  return <>{[...list.filter(a => !a.primary), ...list.filter(a => a.primary)].map(a => (
    <button key={a.code} className={'btn ' + size + ' ' + (a.primary ? 'primary' : DANGER.includes(a.code) ? 'danger ghost' : '')} disabled={busy} onClick={e => { e.stopPropagation(); onRun(a); }}>
      {a.primary && <Icon name={iconFor(a.code)} />}{a.label}
    </button>
  ))}</>;
}
export function iconFor(code: string) {
  return ({ SUBMIT: 'send', CAB: 'chart', CAB_OK: 'chart', AVIZ: 'check', CFP_OK: 'stamp', CFPD_OK: 'stamp', APPROVE: 'approve', SIGN: 'approve', REGISTER: 'check', BUN_PLATA: 'approve', ORDER: 'stamp',
    PAY: 'bank', NEW_AL: 'contract', NEW_LIQ: 'receipt', OPEN_EINV: 'receipt', RETURN: 'refresh', REVISE: 'edit', CLOSE: 'lock', CARRY: 'calendar', RESTITUIRE: 'refresh' } as Record<string, string>)[code] || 'check';
}

// ------------------------------------------------------------------ money
export function MoneyInput({ value, onChange, invalid, autoFocus, placeholder }: { value: number | string; onChange: (n: number) => void; invalid?: boolean; autoFocus?: boolean; placeholder?: string }) {
  const [text, setText] = useState(() => value === '' || value == null ? '' : lei(Number(value)));
  const focused = useRef(false);
  // reformat when not typing, or when the value was changed from outside (e.g. filled by the AI reading)
  useEffect(() => { if (!focused.current || parseAmount(text) !== Number(value || 0)) setText(value === '' || value == null || Number(value) === 0 ? '' : lei(Number(value))); }, [value]);
  return (
    <div className="money-input">
      <input className={'input num ' + (invalid ? 'invalid' : '')} inputMode="decimal" autoFocus={autoFocus} placeholder={placeholder || '0,00'} value={text}
        onFocus={() => { focused.current = true; }} onBlur={() => { focused.current = false; setText(parseAmount(text) ? lei(parseAmount(text)) : ''); }}
        onChange={e => { setText(e.target.value); onChange(parseAmount(e.target.value)); }} />
      <span>lei</span>
    </div>
  );
}

// ------------------------------------------------------------------ partner (beneficiar) with ANAF lookup
export function PartnerPicker({ value, onChange }: { value: Partner | null; onChange: (p: Partner | null) => void }) {
  const toast = useToast();
  const [q, setQ] = useState('');
  const [list, setList] = useState<Partner[]>([]);
  const [busy, setBusy] = useState(false);
  const [manual, setManual] = useState(false);
  useEffect(() => { const t = setTimeout(() => api.get<Partner[]>('/api/alop/partners?q=' + encodeURIComponent(q)).then(setList).catch(() => {}), 200); return () => clearTimeout(t); }, [q]);
  const lookup = async (cui: string) => {
    setBusy(true);
    try { const p = await api.get<Partner>('/api/alop/partners/lookup/' + encodeURIComponent(cui)); onChange(p); toast(`${p.name} — date preluate de la ANAF`, 'ok'); }
    catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); }
  };
  if (value && !manual) return (
    <div className="partner-card">
      <div className="grow"><b>{value.name}</b>{value.source === 'ANAF' && <span className="tag" title="Date verificate la ANAF">ANAF</span>}{value.vat && <span className="tag">plătitor TVA</span>}
        <div className="muted small">{[value.cui && 'CUI ' + value.cui, value.iban, value.address].filter(Boolean).join(' · ')}</div></div>
      <button className="btn sm ghost" onClick={() => setManual(true)}><Icon name="edit" />Modifică</button>
      <button className="icon-btn" title="Fără beneficiar" onClick={() => onChange(null)}><Icon name="x" /></button>
    </div>
  );
  if (manual) return <PartnerForm value={value} onDone={p => { setManual(false); onChange(p); }} onCancel={() => setManual(false)} />;
  const digits = q.replace(/^ro/i, '').replace(/\D/g, '');
  const opts = list.map(p => ({ value: p.id!, label: p.name, sub: [p.cui && 'CUI ' + p.cui, p.iban].filter(Boolean).join(' · ') }));
  return (
    <div className="row" style={{ alignItems: 'stretch' }}>
      <div className="grow" style={{ position: "relative" }}><input className="input" placeholder="Caută după denumire sau scrie CUI-ul…" value={q} onChange={e => setQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && digits.length >= 2) { e.preventDefault(); lookup(digits); } }} />
        {q && opts.length > 0 && <div className="partner-sugg">{opts.slice(0, 6).map(o => <button key={o.value} onClick={() => { onChange(list.find(p => p.id === o.value)!); setQ(''); }}><b>{o.label}</b><span className="muted small">{o.sub}</span></button>)}</div>}
      </div>
      <button className="btn" disabled={busy || digits.length < 2} onClick={() => lookup(digits)} title="Caută CUI-ul la ANAF: denumire, adresă, TVA, nr. Registrul Comerțului">{busy ? <span className="spinner sm" /> : <Icon name="search" />}ANAF</button>
      <button className="btn ghost" onClick={() => setManual(true)} title="Persoană fizică sau partener fără CUI"><Icon name="plus" />Manual</button>
    </div>
  );
}
function PartnerForm({ value, onDone, onCancel }: { value: Partner | null; onDone: (p: Partner) => void; onCancel: () => void }) {
  const toast = useToast();
  const [p, setP] = useState<Partner>(value || { name: '' });
  const save = async () => { try { onDone(await api.post<Partner>('/api/alop/partners', p)); } catch (e: any) { toast(e.message, 'error'); } };
  return (
    <div className="partner-form">
      <div className="form-grid">
        <Field label="Denumire / nume" required><input className="input" autoFocus value={p.name} onChange={e => setP({ ...p, name: e.target.value })} /></Field>
        <Field label="CUI / CNP"><input className="input" value={p.cui || ''} onChange={e => setP({ ...p, cui: e.target.value })} /></Field>
        <Field label="IBAN"><input className="input mono" value={p.iban || ''} onChange={e => setP({ ...p, iban: e.target.value })} placeholder="RO.." /></Field>
        <Field label="Banca / trezoreria"><input className="input" value={p.bank || ''} onChange={e => setP({ ...p, bank: e.target.value })} /></Field>
        <Field label="Adresa" className="span2"><input className="input" value={p.address || ''} onChange={e => setP({ ...p, address: e.target.value })} /></Field>
      </div>
      <div className="row mt"><span className="grow" /><button className="btn sm" onClick={onCancel}>Renunță</button><button className="btn sm primary" disabled={!p.name.trim()} onClick={save}>Salvează partenerul</button></div>
    </div>
  );
}

// ------------------------------------------------------------------ budget lines
export function useBudget(year?: number) {
  const [lines, setLines] = useState<BudgetLine[] | null>(null);
  const y = year || new Date().getFullYear();
  const reload = () => api.get<BudgetLine[]>('/api/alop/budget?year=' + y).then(setLines).catch(() => setLines([]));
  useEffect(() => { reload(); const on = () => reload(); window.addEventListener('alop:changed', on); return () => window.removeEventListener('alop:changed', on); }, [y]);
  return { lines, reload };
}
export function LinePicker({ lines, value, onChange, invalid }: { lines: BudgetLine[]; value: number | null; onChange: (id: number | null) => void; invalid?: boolean }) {
  const opts = useMemo(() => lines.filter(l => l.active).map(l => ({ value: l.id, label: `${l.econ} · ${l.name}`, sub: `SSI ${l.ssi} · program ${l.program} · disponibil ${lei(l.cbAvailable)} lei` })), [lines]);
  return <Combobox value={value} options={opts} onChange={v => onChange(v == null ? null : Number(v))} placeholder="Cod economic, denumire sau cod SSI…" invalid={invalid} />;
}

// ------------------------------------------------------------------ progress of a dosar
export function PhaseBar({ phase }: { phase: Phase }) {
  const idx = phase === 'INCHIS' ? PHASES.length : PHASES.findIndex(p => p.code === phase);
  if (phase === 'ANULAT') return <div className="phase-bar cancelled"><Icon name="x" />Dosar anulat</div>;
  return (
    <ol className="phase-bar">
      {PHASES.map((p, i) => <li key={p.code} className={i < idx ? 'done' : i === idx ? 'current' : ''}><span className="dot">{i < idx ? <Icon name="check" size={13} /> : i + 1}</span><span className="lbl">{p.label}</span></li>)}
      {phase === 'INCHIS' && <li className="done closed"><span className="dot"><Icon name="lock" size={12} /></span><span className="lbl">Închis</span></li>}
    </ol>
  );
}
export function MoneyBar({ d }: { d: Dossier }) {
  const t = d.totals; const base = Math.max(t.reserved, t.committed, 1);
  const items: [string, number, string][] = [['Rezervat', t.reserved, 'res'], ['Angajat', t.committed, 'com'], ['Lichidat', t.liquidated, 'liq'], ['Ordonanțat', t.ordered, 'ord'], ['Plătit', t.paid, 'paid']];
  return (
    <div className="money-bar">
      {items.map(([l, v, c]) => (
        <div key={l} className={'mb-item ' + c}>
          <span className="lbl">{l}</span><b className="num">{lei(v)}</b>
          <span className="track"><span style={{ width: `${Math.min(100, (v / base) * 100)}%` }} /></span>
        </div>
      ))}
    </div>
  );
}

/** Bar of a budget line: paid ⊂ ordered ⊂ reserved, out of the approved credits. */
export function LineBar({ l }: { l: BudgetLine }) {
  const base = Math.max(Number(l.cbApproved), 1);
  const pct = (v: number) => `${Math.min(100, (Number(v) / base) * 100)}%`;
  return (
    <div className="line-bar" title={`Rezervat ${lei(l.cbReserved)} · Ordonanțat ${lei(l.ordered)} · Plătit ${lei(l.paid)} din ${lei(l.cbApproved)} lei`}>
      <span className="res" style={{ width: pct(l.cbReserved) }} /><span className="ord" style={{ width: pct(l.ordered) }} /><span className="paid" style={{ width: pct(l.paid) }} />
    </div>
  );
}

export function useAlopEvent(fn: () => void) {
  useEffect(() => { window.addEventListener('alop:changed', fn); return () => window.removeEventListener('alop:changed', fn); });
}

// ------------------------------------------------------------------ AI reading of a scanned invoice / contract
export interface AiField { value: string; source: 'AI' | 'RULE' | 'BOTH' | 'CALC' | string; confidence: 'HIGH' | 'CHECK' | string; label: string; alternative?: string; note?: string }
export interface AiRead {
  fields: Partial<Record<'suma' | 'tva' | 'cui' | 'furnizor' | 'iban' | 'numar' | 'data' | 'scadenta' | 'cod_angajament', AiField>>;
  partner?: Partner | null; matches: { commitmentId: number; dfId: number; label: string; rest: number; by: string }[];
  ai?: { enabled?: boolean; used?: boolean; model?: string; error?: string }; fileId: string; fileName: string;
}
const SRC: Record<string, string> = { AI: 'AI', RULE: 'reguli', BOTH: 'AI + reguli', CALC: 'calculat' };

/** „Citește cu AI”: upload the scanned document, get proposals for the form (the user checks them before saving). */
export function AiReadBox({ kind, dfId, onRead, warn }: { kind: 'INVOICE' | 'CONTRACT'; dfId?: number; onRead: (r: AiRead) => void; warn?: (r: AiRead) => string[] }) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState<AiRead | null>(null);
  const read = async (file: File) => {
    setBusy(true);
    try {
      const f = await api.upload<{ id: string }>(file);
      const r = await api.post<AiRead>('/api/alop/ai/read', { fileId: f.id, kind, dfId });
      const full = { ...r, fileId: f.id, fileName: file.name };
      setRes(full); onRead(full);
      const n = Object.keys(r.fields).length;
      toast(n ? `Am propus ${n} câmpuri — verificați-le înainte de salvare` : 'Nu am găsit date în document', n ? 'ok' : 'error');
    } catch (e: any) { toast(e.message, 'error'); } finally { setBusy(false); if (input.current) input.current.value = ''; }
  };
  const warnings = res && warn ? warn(res) : [];
  return (
    <div className={'ai-read' + (res ? ' done' : '')} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) read(f); }}>
      <input ref={input} type="file" hidden accept="application/pdf,image/*" onChange={e => { const f = e.target.files?.[0]; if (f) read(f); }} />
      <div className="ai-read-head">
        <span className="ai-badge"><Icon name="sparkles" size={16} /></span>
        <div className="grow">{res ? <><b>{res.fileName}</b><div className="muted small">{res.ai?.used ? `citit cu ${res.ai.model}` : res.ai?.enabled ? 'modelul AI nu a răspuns — citit cu regulile încorporate' : 'citit cu regulile încorporate (modelul AI nu este configurat)'} · fișierul se atașează la dosar</div></>
          : <><b>Citește cu AI {kind === 'INVOICE' ? 'factura' : 'contractul'} scanat{kind === 'INVOICE' ? 'ă' : ''}</b><div className="muted small">Trageți aici PDF-ul sau poza — propun suma, TVA, CUI, IBAN, numărul, datele și codul de angajament.</div></>}</div>
        <button type="button" className="btn sm" disabled={busy} onClick={() => input.current?.click()}>{busy ? <><span className="spinner sm" />Se citește…</> : <><Icon name="upload" />{res ? 'Alt fișier' : 'Alege fișierul'}</>}</button>
      </div>
      {res && Object.keys(res.fields).length > 0 && (
        <div className="ai-chips">{Object.entries(res.fields).map(([k, f]) => f && (
          <span key={k} className={'ai-chip ' + (f.confidence === 'HIGH' ? 'hi' : 'chk')} title={`${SRC[f.source] || f.source}${f.alternative ? ' · altă variantă: ' + f.alternative : ''}${f.note ? ' · ' + f.note : ''}`}>
            {f.label}: <b>{/^\d{4}-\d{2}-\d{2}$/.test(f.value) ? fmtDate(f.value) : ['suma', 'tva'].includes(k) ? lei(Number(f.value)) : f.value}</b>{f.confidence !== 'HIGH' && <Icon name="alert" size={12} />}
          </span>))}
        </div>)}
      {warnings.map((w, i) => <div key={i} className="banner small warn"><Icon name="alert" size={15} /><span>{w}</span></div>)}
    </div>
  );
}
